import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import { Protect } from 'src/services/personal-data-protection/decorators/protect.decorator';
import { AbstractCustomEventTracking } from 'src/services/tracking/abstract/abstract-custom-event-tracking';
import type { IMarkerOptions, IPageLoadedCustomEventData } from 'src/shared/types/tracking';

declare const Munchkin: any;

@Injectable({ providedIn: 'root' })
export class PageLoadedCustomEventTrackingService extends AbstractCustomEventTracking {
    private readonly platformId = inject(PLATFORM_ID);

    private readonly event = 'spaNewPageLoaded';
    private isFirstBrowserRender = true; // the first browser render tracked through GTM
    private isFirstMunchkinTrack = true;

    public track(data: IPageLoadedCustomEventData): void {
        if (isPlatformBrowser(this.platformId)) {
            if (this.isFirstBrowserRender) {
                this.isFirstBrowserRender = false;
            } else {
                this.transport({
                    customEventName: this.event,
                    detail: {
                        virtualUrl: data.origin + data.pathname,
                        virtualPath: data.pathname,
                    },
                });
            }
        }
    }

    @Protect({ requestConsent: false, consentType: 2, disableQueue: false })
    public munchkinTrack(slug: string, markers: string[] | IMarkerOptions[]): void {
        if (isPlatformBrowser(this.platformId)) {
            (async () => {
                while (!Object.prototype.hasOwnProperty.call(window, 'Munchkin')) {
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                }

                if (this.isFirstMunchkinTrack) {
                    const markerValues = this.convertMarkersToString(markers);

                    Munchkin.munchkinFunction('visitWebPage', {
                        url: slug,
                        params: markerValues,
                    });

                    this.isFirstMunchkinTrack = false;
                }
            })();
        }
    }

    private convertMarkersToString(markers: string[] | IMarkerOptions[]): string {
        let result = '';

        if (!markers.length) {
            return result;
        }

        markers.forEach((item) => {
            if (Array.isArray(item.marker)) {
                item.marker.forEach((value) => {
                    result += item.type + ':' + value + ';';
                });
            } else {
                result += item.type + ':' + item.marker + ';';
            }
        });

        return result.substring(0, result.length - 1);
    }

    @Protect({ requestConsent: false, consentType: 2, disableQueue: false })
    public qualifiedIdentify(gaCookie: string): void {
        try {
            if (isPlatformBrowser(this.platformId)) {
                gaCookie &&
                    window?.qualified?.('identify', {
                        ga_client_id: gaCookie,
                    });
            }
        } catch (error) {
            console.error('Unable to call qualified(`identify`) API');
        }
    }
}
